import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { quotationsApi, type QuotationDetail } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  ErrorState,
  LoadingState,
  Panel,
  SegmentedControl,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { openPdf, printPdf, requirePdf, saveBlob } from '../reports/report-parts.js';

type DocumentLanguage = 'ar' | 'en';

/** The query key of one quotation's document in one language. */
export const quotationDocumentKey = (id: string, language?: DocumentLanguage) =>
  language ? ['quotation-document', id, language] : ['quotation-document', id];

/**
 * The quotation DOCUMENT: preview, print and download (D198).
 *
 * All three are the SAME PDF, rendered by the server from the dedicated
 * quotation template — never this web page. What the preview shows is exactly
 * what prints and what is saved.
 *
 * PRINT still records a printed copy through its own call, which changes no
 * status: printing is never acceptance.
 */
export function QuotationDocumentPanel({ quotation }: { readonly quotation: QuotationDetail }) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState<DocumentLanguage>(
    currentLocale() === 'ar' ? 'ar' : 'en',
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fetchDocument = () =>
    quotationsApi.document(quotation.id, language).then(requirePdf);

  const preview = useQuery({
    queryKey: quotationDocumentKey(quotation.id, language),
    queryFn: fetchDocument,
    enabled: previewOpen,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // One object URL per PDF, released as soon as it is replaced or unmounted.
  useEffect(() => {
    if (!preview.data) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(preview.data);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview.data]);

  /** The PDF now on screen, or a fresh one — the same bytes either way. */
  const currentDocument = () =>
    queryClient.fetchQuery({
      queryKey: quotationDocumentKey(quotation.id, language),
      queryFn: fetchDocument,
      staleTime: 30_000,
    });

  const print = useMutation({
    mutationFn: async () => {
      const blob = await currentDocument();
      // Recorded only once there is a document to print.
      await quotationsApi.recordPrint(quotation.id, { locale: language });
      return blob;
    },
    onSuccess: async (blob) => {
      printPdf(blob);
      show({ tone: 'success', message: t('quotations.printed') });
      await queryClient.invalidateQueries({ queryKey: ['quotation', quotation.id] });
    },
  });

  const download = useMutation({
    mutationFn: currentDocument,
    onSuccess: (blob) => saveBlob(blob, `${quotation.quotationNumber}-${language}.pdf`),
  });

  const source = quotation.hasIssuedDocument
    ? 'issued'
    : quotation.status === 'draft'
      ? 'draft'
      : 'reprint';

  return (
    <Panel
      title={t('quotations.document.title')}
      description={t(`quotations.document.source.${source}`)}
    >
      <div className="flex flex-wrap items-center justify-between gap-stack">
        <SegmentedControl<DocumentLanguage>
          label={t('quotations.document.language')}
          value={language}
          onChange={setLanguage}
          options={[
            { value: 'ar', label: t('common.arabic') },
            { value: 'en', label: t('common.english') },
          ]}
        />
        <ActionBar>
          <ActionButton
            priority="tertiary"
            onPress={() => setPreviewOpen((open) => !open)}
            aria-expanded={previewOpen}
          >
            {previewOpen ? t('quotations.document.hidePreview') : t('quotations.document.preview')}
          </ActionButton>
          {/*
            A deleted quotation can still be viewed and downloaded, but no new
            printed copy of it is recorded (D210); the server refuses it too.
          */}
          {quotation.isDeleted ? null : (
            <ActionButton
              priority="secondary"
              onPress={() => print.mutate()}
              isLoading={print.isPending}
            >
              {t('quotations.print')}
            </ActionButton>
          )}
          <ActionButton
            priority="secondary"
            onPress={() => download.mutate()}
            isLoading={download.isPending}
          >
            {t('quotations.document.download')}
          </ActionButton>
        </ActionBar>
      </div>

      {print.error || download.error ? (
        <div className="mt-stack">
          <ErrorState error={(print.error ?? download.error) as Error} />
        </div>
      ) : null}

      {previewOpen ? (
        <div className="mt-stack">
          {preview.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : preview.error ? (
            <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
          ) : previewUrl ? (
            <>
              <iframe
                src={previewUrl}
                title={t('quotations.document.frameTitle', { number: quotation.quotationNumber })}
                className="block h-[80vh] min-h-[28rem] w-full rounded-medium border border-default-200 bg-default-100"
              />
              <div className="mt-stack">
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  onPress={() => preview.data && openPdf(preview.data)}
                >
                  {t('quotations.document.openInTab')}
                </ActionButton>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}
